import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, setLanguagePreference, type AppLanguage } from "../../i18n";

const LABELS: Record<AppLanguage, string> = {
  "pt-BR": "PT",
  en: "EN",
};

/**
 * Alternador de idioma compacto (pill PT/EN). Persiste a escolha no
 * localStorage via `setLanguagePreference` e atualiza o <html lang> através do
 * listener registrado em main.tsx.
 *
 * Visual "Cream & Ink" por padrão; passe `variant="dark"` em fundos escuros.
 *
 * `onSelect` sobrescreve o comportamento padrão (apenas `changeLanguage`): a
 * landing usa isso para também navegar entre `/` (pt) e `/en`, mantendo a URL
 * como fonte de verdade do idioma para SEO.
 */
export const LanguageSwitcher: React.FC<{
  variant?: "light" | "dark";
  className?: string;
  onSelect?: (lng: AppLanguage) => void;
}> = ({ variant = "light", className = "", onSelect }) => {
  const { i18n } = useTranslation();
  const current = (i18n.resolvedLanguage || i18n.language) as string;

  const base =
    variant === "dark"
      ? "border-white/15 bg-white/5 text-white/60"
      : "border-ink/10 bg-white text-ink/50";

  return (
    <div className={`inline-flex items-center gap-0.5 rounded-full border-2 p-0.5 ${base} ${className}`}>
      {SUPPORTED_LANGUAGES.map((lng) => {
        const active = current === lng;
        return (
          <button
            key={lng}
            type="button"
            onClick={() => (onSelect ? onSelect(lng) : setLanguagePreference(lng))}
            aria-pressed={active}
            className={`px-2.5 py-1 text-xs font-bold rounded-full transition-colors cursor-pointer ${
              active
                ? variant === "dark"
                  ? "bg-primary-500 text-ink"
                  : "bg-ink text-cream"
                : "hover:text-ink/80"
            }`}
          >
            {LABELS[lng]}
          </button>
        );
      })}
    </div>
  );
};
